'use client';

import type { ReactNode } from 'react';

import type { SocialAuthProviderItem } from '@eliadmin/core/src/api/customers/customer-social-auth-api';
import { SocialAuthButtons } from '@eliadmin/core/src/components/auth/social-auth-buttons';
import { FormResultMessage } from '@eliadmin/core/src/components/nt/account/shell/form-result';
import { TextInput } from '@eliadmin/core/src/components/nt/forms/controls';
import { describedBy, FormField } from '@eliadmin/core/src/components/nt/forms/form-field';
import { Icon } from '@eliadmin/core/src/components/nt/ui/icon';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

import { PasswordInput } from '../shared/password-input';
import type { SignInFormState } from './use-sign-in-form';

/** Figma „Zabudli ste heslo?“ (221:13176): UI/Button M #0E6A20, underlined. */
export const FORGOT_LINK =
  'nt-button-m rounded text-[#0E6A20] underline underline-offset-2 transition-colors hover:text-[#0B5719] focus-visible:shadow-focus focus-visible:outline-none';

/**
 * Figma „Prihlásenie“ form (221:13176, mobile 269:17445): e-mail and „Heslo“ with „Zobraziť“ 16 px
 * apart, the „Zabudli ste heslo?“ row, then 24 px lower the full-width primary „Prihlásiť sa“ and the
 * lock line (Body/S #5D655D). The social sign-in of the core comes first when the store has
 * providers (Figma has none). „Zapamätať si ma“ is not rendered: there is no such API – the session
 * cookie is the core one. Used on the page and in `StaySignInDialog`; `id` keeps the field ids unique.
 */
export function SignInForm({
  id,
  state,
  forgot,
  emailPlaceholder,
  secureText,
  socialProviders,
  socialReturnUrl,
}: {
  id: string;
  state: SignInFormState;
  /** The „Zabudli ste heslo?“ control (a modal button on the page, a link in the dialog). */
  forgot?: ReactNode;
  emailPlaceholder?: string;
  secureText?: string;
  socialProviders?: SocialAuthProviderItem[];
  /** Where the social sign-in returns to (core callback `returnUrl`). */
  socialReturnUrl?: string;
}) {
  const { t } = useTranslations();
  const { form, submit, error, busy } = state;
  const { errors } = form.formState;
  const emailId = `${id}-email`;
  const passwordId = `${id}-password`;

  return (
    <form
      noValidate
      onSubmit={submit}
      className="flex flex-col gap-6"
      aria-busy={busy || undefined}
    >
      <SocialAuthButtons initialProviders={socialProviders} successRedirectUrl={socialReturnUrl} />

      <div className="flex flex-col gap-4">
        <FormField id={emailId} label={t('text35')} required error={errors.email?.message}>
          <TextInput
            id={emailId}
            type="email"
            inputMode="email"
            autoComplete="username"
            autoCapitalize="off"
            spellCheck={false}
            placeholder={emailPlaceholder || undefined}
            aria-required
            invalid={!!errors.email}
            aria-describedby={describedBy(emailId, { error: errors.email?.message })}
            {...form.register('email')}
          />
        </FormField>
        <FormField id={passwordId} label={t('text191')} required error={errors.password?.message}>
          <PasswordInput
            id={passwordId}
            autoComplete="current-password"
            aria-required
            invalid={!!errors.password}
            aria-describedby={describedBy(passwordId, { error: errors.password?.message })}
            {...form.register('password')}
          />
        </FormField>
        {forgot ? <div className="flex min-h-[22px] items-center justify-end">{forgot}</div> : null}
      </div>

      <div className="flex flex-col gap-4 md:gap-6">
        <Button type="submit" isLoading={busy} className="w-full">
          {t('text189')}
        </Button>
        <FormResultMessage
          result={error ? { status: 'failed', message: error } : null}
          className="empty:-mt-4 md:empty:-mt-6"
        />
        {secureText ? (
          <p className="nt-body-s text-balance text-center text-[#5D655D]">
            <Icon
              name="lock-simple"
              size={20}
              className="mr-1.5 inline-block align-[-5px] max-md:size-4 max-md:align-[-3px] md:mr-2"
            />
            {secureText}
          </p>
        ) : null}
      </div>
    </form>
  );
}
